import {useInfiniteQuery} from '@tanstack/react-query';
import {useState, useSyncExternalStore} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last and numeric-vs-string live in the
// library, not in a per-module copy.
import {sortRecords} from '@flexinet/ui-components';

import {fetchRecords} from '../services/MeteringRecordsService';
import {MeteringRecord} from '../types';

// FNP-534: read-only module. No create/update/delete mutations — the metering
// aggregator owns the MeteringRecord lifecycle. This hook is list-fetch only.

// Module-scoped global filter store (global-filter-store-pattern). Backend-supported
// fields (MeteringRecordsFilterInput):
//   state           -> MeteringRecordState (OPEN | CLOSED)
//   customerId      -> ID
//   meterId         -> ID
//   meterCode       -> String (FNP-538)
//   periodStartFrom -> AWSDateTime (FNP-538; narrows only with meterId set)
//   periodStartTo   -> AWSDateTime (FNP-538; narrows only with meterId set)
// No default filter is seeded — admins see all records on first visit.
let globalFilters: Record<string, string> = {};
const filterListeners: Set<() => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener());
};

// External store wired through useSyncExternalStore (FNP-593): the previous
// useState(globalFilters) + useEffect-registered listener could miss a change
// fired between a consumer's first render and its effect commit, briefly
// desyncing the filter drawer and the list. useSyncExternalStore subscribes
// during render and reads the live snapshot, so there is no race and no tearing.
// getSnapshot returns the same globalFilters reference until notifyFilterChange
// swaps in a new object, satisfying the cached-snapshot requirement.
const subscribeFilters = (listener: () => void) => {
    filterListeners.add(listener);
    return () => {
        filterListeners.delete(listener);
    };
};

const getFiltersSnapshot = (): Record<string, string> => globalFilters;

// Test-only escape hatch — resets the module-scoped store to its default (empty
// filter). Tests share the store, so without this they leak state between cases.
export const __resetMeteringRecordsFilterForTesting = () => {
    notifyFilterChange({});
};

export const useMeteringRecordsFilter = () => {
    const filters = useSyncExternalStore(subscribeFilters, getFiltersSnapshot);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {filters, handleSetFilters, handleClearFilters};
};

// Page size is a server concern fixed by the module (§9), never a user choice.
const PAGE_SIZE = 10;

// NEWEST CREATED FIRST, on `created`: the sort key of record-type-created-index, which
// the list resolver queries with ScanIndexForward false, so first paint agrees with the
// server's own order. `created` is nullable in the schema, but every row on that GSI
// carries it (a GSI only holds items with its sort key). The meterId + periodStart
// path orders by periodStart instead; the client re-sorts those rows by created.
//
// The billing-api resolver drops the `sort` argument (list.ts reads only filter, limit
// and nextToken), so ordering is applied here rather than requested.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<MeteringRecord> = {
    columnAccessor: 'created',
    direction: 'desc',
};

const matchesKeyword = (item: MeteringRecord, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [
        item.customerId,
        item.meterCode,
        item.meterId,
        item.state,
        item.meteringRecordId,
    ];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

export const meteringRecordsQueryKeys = {
    all: ['metering-records'] as const,
    list: (filters: Record<string, string>) => ['metering-records', 'list', filters] as const,
};

export const useMeteringRecords = () => {
    const {filters} = useMeteringRecordsFilter();

    // Component state, not a module-scoped store: the search input (TableSearchForm)
    // and the rows it narrows are rendered by the SAME component, RecordsTable, so
    // there is no sibling for a store to reach (the FNP-815 failure needs two).
    const [keyword, setKeyword] = useState('');

    // Sort is how one operator is viewing the table right now: component state.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<MeteringRecord>>(DEFAULT_SORT_STATUS);

    // Only what the server filters on is in the key. The keyword is applied
    // client-side below, so keying on it would refetch identical pages per keystroke.
    const query = useInfiniteQuery({
        queryKey: meteringRecordsQueryKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit: PAGE_SIZE, filter: filters, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        // Rows are written by the aggregator, not by this UI; 30s stops a remount from
        // refetching while Reload stays the way to see fresh aggregates.
        staleTime: 30_000,
    });

    // FNP-539: the service guarantees page.items is an array even on a GraphQL-level
    // error; filter(Boolean) still keeps a null row out of the table.
    const items: MeteringRecord[] = (query.data?.pages?.flatMap(
        page => (page && 'items' in page && page.items) ? page.items : []) ?? []).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ Client-side filter and sort cover the pages FETCHED SO FAR. Under token
    // pagination the browser cannot order or search rows it has not loaded; that is a
    // property of the model, not a bug (§9). The filter drawer narrows server-side.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus
    );

    const lastPage = query.data?.pages?.[query.data.pages.length - 1];

    return {
        // The loaded set BEFORE keyword filtering: what the footer's
        // "N of M loaded" is measured against.
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingMore: query.isFetchingNextPage,
        // The server's own cursor, never a stand-in: opaque to this client (§9).
        nextToken: query.hasNextPage ? (lastPage?.nextToken ?? null) : null,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => {
            query.refetch();
        },
        handleFilterByKeyword: (newKeyword: string) => setKeyword(newKeyword),
    };
};
